import {useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {FileClock, Fingerprint, ShieldCheck} from "lucide-react";
import {AdminTable} from "../../shared/components/AdminTable";
import {ActionChecklist, OperationsFlow} from "../../shared/components/BusinessBlocks";
import {DataState} from "../../shared/components/DataState";
import {PageSizeSelect} from "../../shared/components/PageSizeSelect";
import {PageToolbar, ToolbarButton, ToolbarInput} from "../../shared/components/PageToolbar";
import {ServerPagination} from "../../shared/components/ServerPagination";
import {useTranslation} from "../../shared/i18n";
import {asList, formatDateTime, formatNumber, getCount} from "../../shared/utils/format";
import {loadEndpoint} from "../control/api";

const DEFAULT_LIMIT = 50;

export const AuditPage = () => {
    const {t} = useTranslation();
    const [draft, setDraft] = useState({actor_id: "", action: "", entity_type: ""});
    // page и limit живут в тех же filters: queryKey уже завязан на объект целиком,
    // а loadEndpoint кладёт любые ключи в query string.
    const [filters, setFilters] = useState({page: 1, limit: DEFAULT_LIMIT});
    const auditQuery = useQuery({
        queryKey: ["admin-audit", filters],
        queryFn: () => loadEndpoint("/api/v2/admin/audit", filters),
        retry: 1,
    });
    const rows = asList(auditQuery.data);
    const total = getCount(auditQuery.data);
    // Ручка аудита отдаёт count/page/limit без next/previous — считаем сами.
    const hasNextPage = filters.page * filters.limit < total;

    // Пустые поля не кладём в filters: иначе они попадают в queryKey и ломают
    // переиспользование кэша между «фильтр не задан» и «фильтр очищен».
    const applyFilters = () => setFilters({
        ...Object.fromEntries(Object.entries(draft).filter(([, value]) => value !== "")),
        page: 1,
        limit: filters.limit,
    });
    const changeLimit = (limit) => setFilters((current) => ({...current, limit, page: 1}));
    const changePage = (page) => setFilters((current) => ({...current, page}));
    const columns = [
        {key: "created_at", label: t("audit.columns.date"), render: (row) => formatDateTime(row.created_at || row.event_time)},
        {key: "actor", label: t("audit.columns.actor"), render: (row) => row.actor || row.actor_name || row.actor_id || "—"},
        {key: "action", label: t("audit.columns.action"), render: (row) => row.action || "—"},
        {key: "entity", label: t("audit.columns.entity"), render: (row) => row.entity || row.entity_type || "—"},
        {key: "request_id", label: t("audit.columns.request"), render: (row) => row.request_id || "—"},
    ];

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">{t("audit.title")}</h1>
                    <p className="admin-page-copy">{t("audit.copy")}</p>
                </div>
                <div className="admin-pill"><ShieldCheck size={14} /> {t("audit.appendOnlyPill")}</div>
            </header>
            <section className="admin-ops-layout">
                <OperationsFlow
                    title={t("audit.flow.title")}
                    copy={t("audit.flow.copy")}
                    steps={[
                        {title: t("audit.flow.steps.action.title"), copy: t("audit.flow.steps.action.copy")},
                        {title: t("audit.flow.steps.snapshot.title"), copy: t("audit.flow.steps.snapshot.copy")},
                        {title: t("audit.flow.steps.search.title"), copy: t("audit.flow.steps.search.copy")},
                    ]}
                    facts={[
                        {label: t("audit.flow.facts.events"), value: formatNumber(getCount(auditQuery.data))},
                        {label: t("audit.flow.facts.mode"), value: t("audit.flow.facts.modeValue")},
                    ]}
                />
                <div className="admin-ops-body">
                    <section className="admin-grid cols-3">
                        <article className="admin-card">
                            <p className="admin-card-title">{t("audit.cards.events.title")}</p>
                            <p className="admin-card-value">{formatNumber(getCount(auditQuery.data))}</p>
                            <p className="admin-card-note">{t("audit.cards.events.note")}</p>
                        </article>
                        <article className="admin-card">
                            <p className="admin-card-title"><FileClock size={14} /> {t("audit.cards.money.title")}</p>
                            <p className="admin-card-note">{t("audit.cards.money.note")}</p>
                        </article>
                        <article className="admin-card">
                            <p className="admin-card-title"><Fingerprint size={14} /> {t("audit.cards.trace.title")}</p>
                            <p className="admin-card-note">{t("audit.cards.trace.note")}</p>
                        </article>
                    </section>
                    <PageToolbar>
                        <ToolbarInput label={t("audit.filters.actor")} value={draft.actor_id} onChange={(event) => setDraft((value) => ({...value, actor_id: event.target.value}))} />
                        <ToolbarInput label={t("audit.filters.action")} value={draft.action} onChange={(event) => setDraft((value) => ({...value, action: event.target.value}))} />
                        <ToolbarInput label={t("audit.filters.entity")} value={draft.entity_type} onChange={(event) => setDraft((value) => ({...value, entity_type: event.target.value}))} />
                        <PageSizeSelect value={filters.limit} onChange={changeLimit} />
                        <ToolbarButton onClick={applyFilters}>{t("audit.filters.apply")}</ToolbarButton>
                    </PageToolbar>
                    <article className="admin-card">
                        <p className="admin-card-title">
                            <ShieldCheck size={14} /> {t("audit.checklist.title")}
                        </p>
                        <ActionChecklist
                            items={[
                                t("audit.checklist.wallets"),
                                t("audit.checklist.organizations"),
                                t("audit.checklist.content"),
                            ]}
                        />
                    </article>
                    {auditQuery.isError ? (
                        <DataState
                            title={t("audit.error.title")}
                            copy={t("audit.error.copy")}
                            items={[
                                t("audit.error.items.who"),
                                t("audit.error.items.what"),
                                t("audit.error.items.when"),
                                t("audit.error.items.link"),
                            ]}
                        />
                    ) : (
                        <>
                            <AdminTable columns={columns} rows={rows} loading={auditQuery.isLoading} emptyText={t("audit.table.empty")} />
                            <ServerPagination
                                count={total}
                                page={filters.page}
                                pageSize={filters.limit}
                                previous={filters.page > 1 ? filters.page - 1 : null}
                                next={hasNextPage ? filters.page + 1 : null}
                                pending={auditQuery.isFetching}
                                onPageChange={changePage}
                            />
                        </>
                    )}
                </div>
            </section>
        </div>
    );
};
